import WeatherForecastSummary from "@/components/weather-forecast-summary/WeatherForecastSummary";
import WeatherInfo from "./weather-info/WeatherInfo";
import SearchModal from "@/components/search-modal/SearchModal";
import SearchBarResult from "@/components/search-bar-result/SearchBarResult";

import { useMemo, useState } from "@wordpress/element";
import { WEATHER_STORE } from "@/store/store";
import { getCurrentLanguage } from "@/helpers/get-current-langage";
import { formatDateWeather } from "@/helpers/format-date";
import { useDispatch, useSelect } from "@wordpress/data";
import { WeatherDispatch, WeatherSelect } from "@/store/type";
import { useFavorites } from "@/hooks/use-favorites";
import { CityFeature } from "@/schemas/city-schema";
import { LocationType } from "@/types/location-params-type";

import "./style.scss";

export const App = () => {
	const lang = getCurrentLanguage();
	const { favorites } = useFavorites();

	const { searchCityById, setDailyForecastStationId } = useDispatch(
		WEATHER_STORE,
	) as WeatherDispatch;

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const {
		cityNameAr,
		cityNameEn,
		cityId,
		cityData,
		observations: { stationData, isLoading: obsLoading },
		error,
		userPosition,
	} = useSelect((select: (store: typeof WEATHER_STORE) => WeatherSelect) => {
		const store = select(WEATHER_STORE);

		return {
			userPosition: store.getCurrentUserLocation(),
			observations: store.getObservationStationState(),
			...store.getCityLocationState(),
			...store.getCityState(),
		};
	});

	const { data } = useMemo(() => {
		if (!stationData || stationData.length === 0)
			return {
				station_id: cityId,
				data: {
					present_weather: undefined,
					temperature: undefined,
					precipitation_1h: undefined,
					precipitation_3h: undefined,
					wind_direction: undefined,
					wind_speed: undefined,
					relative_humidity: undefined,
					visibility: undefined,
					feel_like: undefined,
					date: undefined,
				},
			};

		const findData = stationData[stationData.length - 1].find(
			(item) => item.station_id === cityId,
		);

		return {
			station_id: findData?.station_id,
			data: {
				...findData?.data,
			},
		};
	}, [stationData, cityId]);

	const handleSelectLocation = (value: Pick<CityFeature, "properties">) => {
		searchCityById({
			cityId: value.properties.id,
			cityNameAr: value.properties.name_ar,
			cityNameEn: value.properties.name_en,
		});

		setDailyForecastStationId(cityId);
	};

	return (
		<div className="weather-summary">
			<SearchModal
				handleSelectLocation={(value: Pick<CityFeature, "properties">) =>
					handleSelectLocation(value)
				}
				placeholder="Search Kuwait stations"
				locationType={LocationType.KUWAIT_FORECAST}
				isOpen={isModalOpen}
				closeModal={() => setIsModalOpen(false)}
				cities={cityData}
				favorites={favorites}
				showFav={true}
				userPosition={userPosition}
			/>

			<WeatherForecastSummary
				showFeelLike
				isLoading={obsLoading}
				weatherData={{
					ww: data.present_weather,
					timestep: data.date?.toISOString() ?? undefined,
					temperature: data.temperature,
					feelLike: data.feel_like,
				}}
				searchBar={
					<SearchBarResult
						obsRead={data.date ? formatDateWeather(new Date(data.date)) : "-"}
						showFavButton={true}
						cityNameAr={cityNameAr}
						cityNameEn={cityNameEn}
						locationId={cityId}
						location={lang === "ar" ? cityNameAr : cityNameEn}
						openModal={() => setIsModalOpen(true)}
						isCurrentLocation={userPosition?.cityId === cityId}
					/>
				}
				weatherInfo={
					<WeatherInfo
						isLoading={obsLoading}
						error={error}
						weatherData={data}
					/>
				}
			/>
		</div>
	);
};

export default App;
